import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs';
import {
  AlertTriangle,
  CircleCheck,
  Loader2,
  LucideIconData,
} from 'lucide-angular';

import { AuthService } from '../../../shared/services/auth.service';
import { AuthModalService } from '../../../shared/services/auth-modal.service';
import { ToastService } from '../../../shared/services/toast.service';
import { IconComponent } from '../../../shared/ui/icon/icon.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';

type PageState = 'verifying' | 'verified' | 'already-verified' | 'error';

@Component({
  selector: 'app-email-verification-page',
  imports: [IconComponent, ButtonComponent],
  templateUrl: './email-verification-page.component.html',
  styleUrl: './email-verification-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailVerificationPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);
  private readonly toastService = inject(ToastService);

  protected readonly state = signal<PageState>('verifying');
  protected readonly resending = signal(false);

  protected readonly spinnerIcon: LucideIconData = Loader2;
  protected readonly successIcon: LucideIconData = CircleCheck;
  protected readonly errorIcon: LucideIconData = AlertTriangle;

  private userId = 0;

  ngOnInit(): void {
    const params = this.route.snapshot.paramMap;
    const idRaw = params.get('id');
    const token = params.get('token');

    const id = Number(idRaw);
    if (!idRaw || Number.isNaN(id) || !token) {
      this.state.set('error');
      return;
    }

    this.userId = id;

    this.authService.verifyEmail(id, token).subscribe({
      next: () => this.state.set('verified'),
      error: (err) => {
        if (this.isAlreadyVerifiedError(err)) {
          this.state.set('already-verified');
          return;
        }
        this.state.set('error');
      },
    });
  }

  /**
   * El backend devuelve `422` con `error: "Esta cuenta ya ha sido verificada
   * anteriormente."` cuando el token es válido pero la cuenta ya pasó por el
   * flujo. Lo distinguimos por el texto porque el status 422 también lo usa
   * el backend para fallos genéricos de validación.
   */
  private isAlreadyVerifiedError(err: unknown): boolean {
    const httpErr = err as { status?: number; error?: { error?: unknown } };
    if (httpErr?.status !== 422) return false;
    const msg = httpErr.error?.error;
    return typeof msg === 'string' && /verificada/i.test(msg);
  }

  protected openLoginModal(): void {
    this.authModalService.open('login');
  }

  protected onResend(): void {
    if (!this.userId || this.resending()) return;

    this.resending.set(true);
    this.authService
      .resendVerificationEmail(this.userId)
      .pipe(finalize(() => this.resending.set(false)))
      .subscribe({
        next: () => {
          this.toastService.show({
            type: 'info',
            message: 'Correo reenviado. Revisa tu bandeja de entrada.',
          });
        },
        error: () => {
          this.toastService.show({
            type: 'error',
            message: 'No se pudo reenviar el correo. Inténtalo de nuevo.',
          });
        },
      });
  }
}
